import React, { useState, useEffect } from "react";

// Material UI
import {
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Typography,
  Box,
  Container,
  TableContainer,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Divider,
  Paper,
  Table,
  Tooltip,
} from "@mui/material";
// Icons
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
// Components
import { capitalizeWords } from "../../utils/functions";
import { useTaxonomy } from "../../contexts/TaxonomyContext";
import {
  parseAnchor,
  isValidWebsite,
  getWebsiteValue,
} from "../../utils/functions";

export function GenerateSuppliersData({ suppliersToShow, existingSuppliers }) {
  // Accordion text style change on Open/Close
  const [isAiSupplierOpen, setIsAiSupplierOpen] = useState(true);
  const { taxonomyPath } = useTaxonomy();
  // Auto-expand accordion when new data arrives
  useEffect(() => {
    if (suppliersToShow && suppliersToShow.length > 0) {
      setIsAiSupplierOpen(true);
    }
  }, [suppliersToShow]);

  return (
    <>
      <Container
        maxWidth={false}
        sx={{ width: "80vw", boxShadow: "none", mt: 0 }}
      >
        {/* Show taxonomy path at top-left */}
        {taxonomyPath && (
          <Typography variant="subtitle3" sx={{ py: 2, fontWeight: 300 }}>
            {capitalizeWords(taxonomyPath)}
          </Typography>
        )}
        <Accordion
          expanded={isAiSupplierOpen}
          onChange={(event, expanded) => setIsAiSupplierOpen(expanded)}
        >
          <AccordionSummary
            expandIcon={<ExpandMoreIcon />}
            aria-controls="supplier-content"
            id="suppliers-new"
          >
            <Typography variant="h6">Suppliers</Typography>
          </AccordionSummary>
          <AccordionDetails>
            <Box sx={{ display: "flex", justifyContent: "space-between", my: 2 }}>
              <Typography variant="subtitle1">Suppliers Table</Typography>
            </Box>
            <Divider sx={{ my: 1 }} />
            <Box>
              <SuppliersFinderToShowTable suppliersToShow={suppliersToShow} />
            </Box>
            <Box
              sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", pt: 3, mb: 2, mr: { xs: 0, md: "2rem" } }}
            >
              <Typography variant="subtitle1">
                Existing Suppliers Table
              </Typography>
            </Box>
            <Divider sx={{ my: 1 }} />
            <Box>
              <ExistingSuppliersTable existingSuppliers={existingSuppliers} />
            </Box>
          </AccordionDetails>
        </Accordion>
        {/* <Divider /> */}
      </Container>
    </>
  );
}

const SuppliersFinderToShowTable = ({ suppliersToShow }) => {
  if (!suppliersToShow || suppliersToShow.length === 0) {
    return (
      <Typography variant="body2" sx={{ color: "text.secondary" }}>
        More information is required or wait for further updates.
      </Typography>
    );
  }

  const headers = [
    "Supplier Name",
    "National Status",
    "Service Type",
    "Accreditations",
    "Turnover",
    "Description",
    "Website",
  ];

  return (
    <TableContainer component={Paper} variant="outlined">
      <Table
        sx={{ minWidth: 800 }}
        stickyHeader
        aria-label="suppliers table"
        size="small"
      >
        <TableHead>
          <TableRow>
            {headers.map((header, index) => (
              <TableCell key={index}> {header} </TableCell>
            ))}
          </TableRow>
        </TableHead>
        <TableBody>
          {suppliersToShow.map((item, index) => {
            const website = getWebsiteValue(item);
            const isWebsiteValid = isValidWebsite(website);
            const parsedTitle = parseAnchor(item["Supplier Name"]);
            return (
              <TableRow key={index} hover>
                <TableCell>
                  {parsedTitle ? (
                    <a
                      href={parsedTitle.href}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      {parsedTitle.text.length > 30
                        ? capitalizeWords(parsedTitle.text)
                        : capitalizeWords(parsedTitle.text)}
                    </a>
                  ) : (
                    "-"
                  )}
                </TableCell>
                <TableCell>{item["National Status"]}</TableCell>
                <TableCell>{item["Service Type"]}</TableCell>
                <TableCell>{item.Accreditations}</TableCell>
                <TableCell sx={{ whiteSpace: "nowrap" }}>
                  {item.Turnover}
                </TableCell>
                <TableCell
                  sx={{
                    minWidth: 200,
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                  }}
                >
                  <Tooltip title={item.Description} arrow>
                    <span
                      style={{
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        display: "inline-block",
                        maxWidth: "100%",
                      }}
                    >
                      {item.Description.length > 60
                        ? item.Description.slice(0, 60) + "..."
                        : item.Description}
                    </span>
                  </Tooltip>
                </TableCell>
                <TableCell
                  sx={{
                    maxWidth: 160,
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                  }}
                >
                  {isWebsiteValid ? (
                    <Tooltip title={website} arrow>
                      <a
                        href={
                          website.startsWith("http")
                            ? website
                            : `https://${website}`
                        }
                        target="_blank"
                        rel="noopener noreferrer"
                        style={{
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          display: "inline-block",
                          maxWidth: "100%",
                        }}
                      >
                        {website}
                      </a>
                    </Tooltip>
                  ) : (
                    "-"
                  )}
                </TableCell>
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
    </TableContainer>
  );
};

const ExistingSuppliersTable = ({ existingSuppliers }) => {
  if (!existingSuppliers || existingSuppliers.length === 0) {
    return (
      <Typography variant="body2" sx={{ color: "text.secondary" }}>
        More information is required or wait for further updates.
      </Typography>
    );
  }
  const headers = [
    "Supplier Name",
    "Description",
    "Category L1",
    "Category L2",
    "Category L3",
  ];
  return (
    <TableContainer component={Paper} variant="outlined">
      <Table
        sx={{ minWidth: 800 }}
        stickyHeader
        aria-label="suppliers table"
        size="small"
      >
        <TableHead>
          <TableRow>
            {headers.map((header, index) => (
              <TableCell key={index}> {header} </TableCell>
            ))}
          </TableRow>
        </TableHead>
        <TableBody>
          {existingSuppliers &&
            existingSuppliers.map((item, index) => (
              <TableRow key={index} hover>
                <TableCell>{item["Supplier Name"]}</TableCell>
                <TableCell
                  sx={{
                    minWidth: 200,
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                  }}
                >
                  <Tooltip title={item.Description} arrow>
                    <span
                      style={{
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        display: "inline-block",
                        maxWidth: "100%",
                      }}
                    >
                      {item.Description.length > 200
                        ? item.Description.slice(0, 200) + "..."
                        : item.Description}
                    </span>
                  </Tooltip>
                </TableCell>
                <TableCell>{capitalizeWords(item["Category L1"])}</TableCell>
                <TableCell sx={{ whiteSpace: "nowrap" }}>
                  {capitalizeWords(item["Category L2"])}
                </TableCell>
                <TableCell sx={{ whiteSpace: "nowrap" }}>
                  {capitalizeWords(item["Category L3"])}
                </TableCell>
              </TableRow>
            ))}
        </TableBody>
      </Table>
    </TableContainer>
  );
};
